import './applicantAddCoapplicantPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { NO, YES, YesOrNo } from '../../../store/applicantStore/types/YesOrNo';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    addCoapplicant,
    disableCoapplicantFailedMessage,
    isDisablingCoapplicant,
    disableCoapplicant,
    setAddCoapplicant,
  } = useApplicantStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      addCoApplicantValue: addCoapplicant,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<YesOrNo>): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    setIsInitialSelection(isEmpty(addCoapplicant) && isValidClickEvent(event));
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { addCoApplicantValue } = values;

    if (!addCoApplicantValue) {
      return;
    }

    setAddCoapplicant(addCoApplicantValue);

    if (addCoApplicantValue === NO) {
      const disabledCoapplicant = await disableCoapplicant();

      if (disabledCoapplicant) {
        navigate(next());

        return;
      }
    }

    navigate(next());
  };

  useEffect(() => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-add-coapplicant-page" sx={theme.pageLayout}>
        <header data-testid={locator.pages.applicantAddCoapplicant.header.root}>
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.applicantAddCoapplicant.header.previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.applicantAddCoapplicant.header.toggleDrawerButton
            }
            type="button"
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.applicantAddCoapplicant.progress.root}
            value={useWorkflowStore.getState().computed.progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.applicantAddCoapplicant.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantAddCoapplicant.form.h1}>
                {t('ApplicantAddCoapplicantPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantAddCoapplicant.form.h2}>
                {t('ApplicantAddCoapplicantPage.subtitle')}
              </h2>

              {!isEmpty(disableCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantAddCoapplicant.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {disableCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  defaultValue={addCoapplicant}
                  labelPlacement="below"
                  name="addCoApplicantValue"
                  options={[
                    {
                      label: t('ApplicantAddCoapplicantPage.yes'),
                      value: YES,
                      testId:
                        locator.pages.applicantAddCoapplicant.form.radioOption1,
                    },
                    {
                      label: t('ApplicantAddCoapplicantPage.no'),
                      value: NO,
                      testId:
                        locator.pages.applicantAddCoapplicant.form.radioOption2,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.applicantAddCoapplicant.form.radioGroupRoot
                  }
                  variant="inline"
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <LoadingButton
                  color="primary"
                  data-testid={
                    locator.pages.applicantAddCoapplicant.form.submitButton
                  }
                  disabled={isEmpty(addCoapplicant)}
                  loading={isDisablingCoapplicant}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('ApplicantAddCoapplicantPage.continue')}
                </LoadingButton>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantAddCoapplicant.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantAddCoapplicantPage';
